import moment from 'moment';
import ky from 'ky';
import isEmpty from 'lodash/isEmpty';
import { z } from 'zod';
import { ENV } from 'src/utils/env';
import { getHeadersWithBearerToken } from 'src/utils/get-auth-with-bearer';

const STATUS_IMAGE_SCHEMA = z.object({
  url: z.string(),
  timestamp: z.string().or(z.number()),
});

const PULL_STATUS_IMAGE_SCHEMA = z.object({
  details: z.array(STATUS_IMAGE_SCHEMA),
});

export type PullStatusImageSchema = z.infer<typeof PULL_STATUS_IMAGE_SCHEMA>;

export interface GetStatusImagePromiseParams {
  display_id: string;
  surrounding_count?: number;
  timestamp?: string;
}

export const getStatusImagePromise = async ({
  display_id,
  surrounding_count,
  timestamp,
}: GetStatusImagePromiseParams) => {
  try {
    const headers = getHeadersWithBearerToken();

    const search = new URLSearchParams();

    if (surrounding_count) {
      search.append('surrounding_count', surrounding_count.toString());
    }

    if (timestamp) {
      search.append('timestamp', timestamp);
    }

    const parsedSearch = search.size > 0 ? `?${search.toString()}` : '';

    const response = await ky
      .get(`${ENV.API_URL}/pull_status_image/${display_id}/${parsedSearch}`, {
        headers,
      })
      .json();

    const result = PULL_STATUS_IMAGE_SCHEMA.safeParse(response);

    if (!result.success) {
      return [
        {
          url: `${window.location.origin}/image-placeholder.jpg`,
          timestamp: Date.now(),
        },
      ];
    }

    let res = result.data.details;

    if (timestamp) {
      const passedTime = new Date(Number(timestamp) * 1000);

      const filterByTimestamp = result.data.details?.filter((item) => {
        const currentTime = new Date(Number(item?.timestamp) * 1000);
        return moment(passedTime).isSame(currentTime, 'day') || moment(passedTime).isAfter(currentTime, 'day');
      });

      if (!isEmpty(filterByTimestamp)) {
        res = filterByTimestamp;
      }
    }

    return res?.sort((a, b) => Number(b.timestamp) - Number(a.timestamp));
  } catch (error) {
    console.error('Error getting status image:', error);
    return [
      {
        url: `${window.location.origin}/image-placeholder.jpg`,
        timestamp: Date.now(),
      },
    ];
  }
};

export type PullStatusImageResult = Awaited<ReturnType<typeof getStatusImagePromise>>;
